import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { useSiteSettings } from "../../lib/settings";
import { resolveImage } from "../admin/ImageUpload";

/* ================================================================
 * Home MediaCenterSection — Instagram-style teaser mirroring /media
 *   Row 1: Story rings per category
 *   Row 2: Compact 3/4/6-column square grid → tiles link to /media/:slug
 *   Bottom: "Tümünü Gör → /media" CTA
 * ================================================================ */

/* Ring colors are stable by key; labels come from Site Settings so admin
   can rename them ("Zirve" → "Etkinlik", etc.) without code changes. */
const CATEGORY_RINGS = {
  news:        "from-[#22346B] via-[#4E6BC7] to-[#22346B]",
  video:       "from-[#c9184a] via-[#ff4d6d] to-[#8338ec]",
  event:       "from-[#ff9500] via-[#ffb400] to-[#ff5e00]",
  blog:        "from-[#38b6ff] via-[#3a86ff] to-[#22346B]",
  press:       "from-[#8B8D93] via-[#22346B] to-[#101F4A]",
  publication: "from-[#22346B] via-[#8338ec] to-[#22346B]",
};

const DEFAULT_LABELS = {
  news: "Haberler",
  video: "Video",
  event: "Etkinlik",
  blog: "Blog",
  press: "Basında Biz",
  publication: "Yayınlar",
};

const useCategoryMeta = () => {
  const s = useSiteSettings();
  const overrides = Array.isArray(s?.media_categories) ? s.media_categories : [];
  const labelByKey = { ...DEFAULT_LABELS };
  overrides.forEach((m) => { if (m && m.key && m.label) labelByKey[m.key] = m.label; });
  const order = overrides.length
    ? overrides.map((m) => m.key).filter((k) => k in CATEGORY_RINGS)
    : Object.keys(DEFAULT_LABELS);
  return order.map((key) => ({ key, label: labelByKey[key], ring: CATEGORY_RINGS[key] }));
};

const catLabel = (key, meta) => {
  if (Array.isArray(meta)) {
    const found = meta.find((x) => x.key === key);
    if (found?.label) return found.label;
  }
  return DEFAULT_LABELS[key] || key;
};

const timeAgo = (iso, lang) => {
  if (!iso) return "";
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.max(0, now - then);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor(diff / 60000);
  const suf = lang === "tr" ? { d: "g", h: "s", m: "dk", ago: "önce" } : { d: "d", h: "h", m: "m", ago: "ago" };
  if (d >= 7) return new Date(iso).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-GB", { day: "2-digit", month: "short" });
  if (d >= 1) return `${d}${suf.d} ${suf.ago}`;
  if (h >= 1) return `${h}${suf.h} ${suf.ago}`;
  if (m >= 1) return `${m}${suf.m} ${suf.ago}`;
  return lang === "tr" ? "az önce" : "just now";
};

/* --- Grid tile (Instagram profile square) --- */
const GridTile = ({ n, lang, index = 0 }) => {
  const isReel = !!n.video_url || n.category === "video";
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: (index % 12) * 0.02 }}
      data-testid={`home-grid-tile-${n.slug}`}
    >
      <Link to={`/media/${n.slug}`} className="block group">
        <div className="relative aspect-square overflow-hidden bg-brand-navy/5">
          <img
            src={resolveImage(n.cover_image)}
            alt={n.title?.[lang] || n.title?.tr || ""}
            className="w-full h-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-105"
            loading="lazy"
          />
          {isReel ? (
            <span className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 backdrop-blur-sm text-white flex items-center justify-center pointer-events-none">
              <Play size={12} strokeWidth={2} fill="currentColor" />
            </span>
          ) : null}
        </div>
        <div className="mt-1.5 md:mt-2 px-0.5">
          <div className="text-[0.55rem] tracking-[0.22em] uppercase text-brand-primary font-medium truncate">
            {catLabel(n.category)}
          </div>
          <div className="mt-0.5 text-xs md:text-sm font-medium text-brand-navy leading-snug line-clamp-2 group-hover:text-brand-primary transition-colors">
            {n.title?.[lang] || n.title?.tr || ""}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

/* --- Story viewer (reused from media page) --- */
const StoryViewer = ({ items, category, onClose, lang }) => {
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const item = items[idx];
  const timerRef = React.useRef(null);
  const paused = React.useRef(false);

  React.useEffect(() => {
    setProgress(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (paused.current) return;
      setProgress((p) => {
        if (p >= 100) {
          if (idx + 1 < items.length) { setIdx(idx + 1); return 0; }
          clearInterval(timerRef.current); onClose(); return 100;
        }
        return p + 2;
      });
    }, 100);
    return () => timerRef.current && clearInterval(timerRef.current);
     
  }, [idx]);

  const next = () => idx + 1 < items.length ? (setIdx(idx + 1), setProgress(0)) : onClose();
  const prev = () => idx > 0 ? (setIdx(idx - 1), setProgress(0)) : null;

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
     
  }, [idx]);

  if (!item) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
      data-testid="home-story-viewer"
      onMouseDown={() => { paused.current = true; }}
      onMouseUp={() => { paused.current = false; }}
      onTouchStart={() => { paused.current = true; }}
      onTouchEnd={() => { paused.current = false; }}
    >
      <div className="relative h-full aspect-[9/16] max-w-full max-h-[100dvh] bg-black">
        <img src={resolveImage(item.cover_image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute top-2 left-2 right-2 flex gap-1 z-30">
          {items.map((_, i) => (
            <div key={i} className="h-[3px] flex-1 bg-white/25 rounded overflow-hidden">
              <div className="h-full bg-white" style={{ width: i < idx ? "100%" : i === idx ? `${progress}%` : "0%" }} />
            </div>
          ))}
        </div>
        <div className="absolute top-6 left-4 right-4 z-30 flex items-center justify-between text-white">
          <div className="text-sm font-medium">{catLabel(category)}</div>
          <button onClick={onClose} className="p-1"><X size={22} strokeWidth={1.5} /></button>
        </div>
        <div className="absolute inset-x-0 bottom-0 p-5 pb-8 z-30 text-white bg-gradient-to-t from-black/80 via-black/40 to-transparent">
          <div className="text-[0.6rem] tracking-[0.22em] uppercase text-white/70 mb-1">{timeAgo(item.published_at, lang)}</div>
          <h3 className="font-serif text-xl md:text-2xl leading-tight">
            {item.title?.[lang] || item.title?.tr || ""}
          </h3>
          <Link to={`/media/${item.slug}`} onClick={onClose} className="mt-4 inline-flex items-center gap-2 bg-white text-brand-navy px-4 py-2 text-[0.66rem] tracking-[0.24em] uppercase font-medium">
            Yazının Devamı <ArrowRight size={12} strokeWidth={1.5} />
          </Link>
        </div>
        <button onClick={prev} className="absolute inset-y-0 left-0 w-1/3 z-20" aria-label="Önceki" />
        <button onClick={next} className="absolute inset-y-0 right-0 w-1/3 z-20" aria-label="Sonraki" />
        <button onClick={prev} className="hidden md:flex absolute -left-14 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center">
          <ChevronLeft size={20} />
        </button>
        <button onClick={next} className="hidden md:flex absolute -right-14 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center">
          <ChevronRight size={20} />
        </button>
      </div>
    </motion.div>
  );
};

/* --- Main section --- */
const MediaCenterSection = ({ items = [] }) => {
  const { lang } = useI18n();
  const meta = useCategoryMeta();
  const [story, setStory] = useState(null);

  const groups = useMemo(() => {
    const m = {};
    items.forEach((n) => { const k = n.category || "news"; if (!m[k]) m[k] = []; m[k].push(n); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (b.published_at || "").localeCompare(a.published_at || "")));
    return m;
  }, [items]);

  const feed = useMemo(
    () => [...items].sort((a, b) => (b.published_at || "").localeCompare(a.published_at || "")).slice(0, 12),
    [items],
  );

  const rings = meta
    .map((m) => ({ ...m, items: groups[m.key] || [] }))
    .filter((r) => r.items.length > 0);

  if (items.length === 0) return null;

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #FAFAFA 0%, #F3F4F6 100%)" }}
      data-testid="home-media-center"
    >
      {/* Navy header strip */}
      <div className="bg-brand-navy text-white" data-testid="home-media-header-strip">
        <div className="container-x py-6 md:py-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="h-px w-8 bg-white/60" />
            <span className="text-[0.6rem] tracking-[0.32em] uppercase text-white/80 font-medium">Medya Merkezi</span>
          </div>
          <h2 className="font-serif text-white leading-tight" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.25rem)" }}>
            Haberler, Video ve Basın
          </h2>
        </div>
      </div>

      {/* Story rings — larger, ~4 visible per row on typical screens, scroll right for more */}
      {rings.length ? (
        <div className="border-b border-black/5 bg-white">
          <div className="container-x py-5 md:py-7">
            <div className="flex gap-5 md:gap-8 overflow-x-auto scrollbar-thin -mx-2 px-2 snap-x snap-mandatory md:snap-none">
              {rings.map((r) => (
                <button
                  type="button"
                  key={r.key}
                  onClick={() => setStory({ category: r.key, items: r.items })}
                  className="flex flex-col items-center flex-shrink-0 group focus:outline-none snap-start"
                  data-testid={`home-story-ring-${r.key}`}
                >
                  <span className={`p-[3px] rounded-full bg-gradient-to-tr ${r.ring}`}>
                    <span className="block p-[3px] rounded-full bg-white">
                      <span className="block w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 rounded-full overflow-hidden bg-brand-navy">
                        <img src={resolveImage(r.items[0].cover_image)} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                      </span>
                    </span>
                  </span>
                  <span className="mt-2 text-xs md:text-sm text-brand-navy font-medium max-w-[112px] text-center truncate">
                    {r.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {/* Instagram-profile compact grid — max 2 rows across all breakpoints */}
      <div className="container-x pt-4 md:pt-5 pb-8 md:pb-12 relative">
        <div className="ig-grid grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3">
          {feed.map((n, i) => (
            <GridTile key={n.id} n={n} lang={lang} index={i} />
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link
            to="/media"
            className="inline-flex items-center gap-2 border border-brand-navy hover:bg-brand-navy hover:text-white text-brand-navy px-8 py-3.5 text-[0.66rem] tracking-[0.28em] uppercase font-medium transition-colors"
            data-testid="home-media-cta"
          >
            Tüm Medyaya Git <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </div>

      <AnimatePresence>
        {story ? (
          <StoryViewer items={story.items} category={story.category} lang={lang} onClose={() => setStory(null)} />
        ) : null}
      </AnimatePresence>

      <style>{`
        .scrollbar-thin::-webkit-scrollbar { height: 4px; width: 4px; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: rgba(34,52,107,0.15); border-radius: 2px; }
        .ig-grid > *:nth-child(n+7)  { display: none; }
        @media (min-width: 768px) {
          .ig-grid > *:nth-child(n+7)  { display: block; }
          .ig-grid > *:nth-child(n+9)  { display: none; }
        }
        @media (min-width: 1024px) {
          .ig-grid > *:nth-child(n+9)  { display: block; }
          .ig-grid > *:nth-child(n+13) { display: none; }
        }
      `}</style>
    </section>
  );
};

export default MediaCenterSection;
